import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'
import {
  useStudioPlusCampaign,
  useStudioPlusPrice,
  useExistingPaymentMethods,
  useZuoraAccount,
} from '@/components/payment/queries'
import { useNotifications } from '@/components/rateConnect/queries'
import { selectedAccommodationId, useAccommodationsByOrgId } from '@/layouts/queries'
import { useSessionValues } from '@/composables/useSessionValues'
import { RC_BLOCKED_COUNTRIES } from '@/constants/rcBlockedCountries'
import { countries } from '@/constants/countries'
import { storage } from '@/utils/storageService'
import type { PaymentMethod, StudioPlusPriceResponse } from '@/services/premiumService/PremiumService'

export const RC_PLUS_CROSS_SELL_VOUCHER_CODE = 'PLUS302026'
export const RC_PLUS_CROSS_SELL_DISCOUNT_PERCENTAGE = 0.3
export const RC_PLUS_CROSS_SELL_POPUP_REMIND_LATER_DISMISSED_KEY = 'rc_plus_cross_sell_popup_remind_later_dismissed'

export const RC_PLUS_CROSS_SELL_INLINE_BANNER_SOURCE = 'Inline Plus banner RC Performance'
export const RC_PLUS_CROSS_SELL_POPUP_SOURCE = 'Plus Cross sell Pop-up'

export type RcPlusCrossSellSource =
  | typeof RC_PLUS_CROSS_SELL_INLINE_BANNER_SOURCE
  | typeof RC_PLUS_CROSS_SELL_POPUP_SOURCE

export const RC_PLUS_CROSS_SELL_BACKEND_SOURCE_GENERAL = 'general'
export const RC_PLUS_CROSS_SELL_BACKEND_SOURCE_BANNER = 'rc_plus_cross_sell_banner'
export const RC_PLUS_CROSS_SELL_BACKEND_SOURCE_POPUP = 'rc_plus_cross_sell_popup'

export type RcPlusCrossSellBackendSource =
  | typeof RC_PLUS_CROSS_SELL_BACKEND_SOURCE_GENERAL
  | typeof RC_PLUS_CROSS_SELL_BACKEND_SOURCE_BANNER
  | typeof RC_PLUS_CROSS_SELL_BACKEND_SOURCE_POPUP

export function mapToBackendSubscriptionSource(source: RcPlusCrossSellSource): RcPlusCrossSellBackendSource {
  switch (source) {
    case RC_PLUS_CROSS_SELL_INLINE_BANNER_SOURCE:
      return RC_PLUS_CROSS_SELL_BACKEND_SOURCE_BANNER
    case RC_PLUS_CROSS_SELL_POPUP_SOURCE:
      return RC_PLUS_CROSS_SELL_BACKEND_SOURCE_POPUP
    default:
      return RC_PLUS_CROSS_SELL_BACKEND_SOURCE_GENERAL
  }
}

const isRcPlusCrossSellEnabled = import.meta.env.VITE_RC_PLUS_CROSS_SELL_ENABLED === 'true'

const isSubscriptionModalOpen = ref(false)
const isPopupOpen = ref(false)
const isPostRcPurchaseSession = ref(false)
const hasClosedPopup = ref(false)
const isRemindMeLaterDismissed = ref(storage.getItem(RC_PLUS_CROSS_SELL_POPUP_REMIND_LATER_DISMISSED_KEY) === 'true')
const isPopupPausedForSubscriptionFlow = ref(false)
const subscriptionTrackingSource = ref<RcPlusCrossSellSource>(RC_PLUS_CROSS_SELL_INLINE_BANNER_SOURCE)

export function showPlusUpsellPopup(): void {
  if (!isRcPlusCrossSellEnabled) return

  isPostRcPurchaseSession.value = true
  hasClosedPopup.value = false
}

export const RC_PLUS_UPSELL_RATE_UNAVAILABILITY_NOTIFICATION_TYPES = [
  'LIVE_RATE_UNAVAILABILITY_100_PERCENT',
  'RATE_UNAVAILABILITY_100_PERCENT',
] as const

export type PlusUpsellSubscribeAction = 'continue' | 'add-payment-method' | 'login-as-admin' | null

export interface UseRcPlusCrossSellReturn {
  isEligibilityLoading: ComputedRef<boolean>
  isPlusUpsellEligible: ComputedRef<boolean>
  hasValidDefaultPaymentMethod: ComputedRef<boolean>
  plusUpsellSubscribeAction: ComputedRef<PlusUpsellSubscribeAction>
  showInlineBanner: ComputedRef<boolean>
  studioPlusPrice: ComputedRef<StudioPlusPriceResponse | null | undefined>
  isPriceFetching: Ref<boolean>
  discountedMonthlyPrice: ComputedRef<number>
  monthlyPrice: ComputedRef<number>
  totalPriceBeforeTaxes: ComputedRef<number>
  baseSubscriptionFullTermPrice: ComputedRef<number>
  defaultPaymentMethod: ComputedRef<PaymentMethod | undefined>
  isSubscriptionModalOpen: Ref<boolean>
  openSubscriptionModal: () => void
  closeSubscriptionModal: () => void
  subscriptionTrackingSource: Ref<RcPlusCrossSellSource>
  setSubscriptionTrackingSource: (source: RcPlusCrossSellSource) => void
  displayPopup: ComputedRef<boolean>
  isPopupOpen: Ref<boolean>
  closePopup: () => void
  dismissPopupRemindMeLater: () => void
  pausePopupForSubscriptionFlow: () => void
  openPopup: () => void
}

export function useRcPlusCrossSell(): UseRcPlusCrossSellReturn {
  const { currency, hasAdminOrSupport, selectedOrganisationId } = useSessionValues()
  const { currentAccommodation } = useAccommodationsByOrgId(selectedOrganisationId)
  const {
    isFetching: isCampaignFetching,
    isExpiredSubscription,
    isPlus,
    isPro,
  } = useStudioPlusCampaign(selectedAccommodationId)

  const { zuoraAccountData, isLoading: isZuoraDataLoading } = useZuoraAccount()
  const countryCode = computed(
    () => countries.find(country => country.name === zuoraAccountData.value?.billToContact?.country)?.code
  )

  const { studioPlusPrice, isFetching: isPriceFetching } = useStudioPlusPrice({
    selectedAccommodationId,
    currency,
    countryCode,
    postalCode: computed(() => zuoraAccountData.value?.billToContact?.zipCode),
    countryState: computed(() => zuoraAccountData.value?.billToContact?.state),
    taxId: computed(() => zuoraAccountData.value?.taxInfo?.exemptCertificateId),
    voucherCode: RC_PLUS_CROSS_SELL_VOUCHER_CODE,
  })
  const { existingPaymentMethods, isFetching: isExistingPaymentMethodsFetching } =
    useExistingPaymentMethods(selectedAccommodationId)
  const {
    notificationsData,
    isFetching: isNotificationsFetching,
    isSuccess: isNotificationsSuccess,
  } = useNotifications('RATE_CONNECT')

  const discountedMonthlyPrice = computed(() => {
    return (studioPlusPrice.value?.baseSubscriptionMonthlyPrice ?? 0) * (1 - RC_PLUS_CROSS_SELL_DISCOUNT_PERCENTAGE)
  })

  const monthlyPrice = computed(() => studioPlusPrice.value?.baseSubscriptionMonthlyPrice ?? 0)
  const totalPriceBeforeTaxes = computed(() => studioPlusPrice.value?.totalPriceBeforeTaxes ?? 0)
  const baseSubscriptionFullTermPrice = computed(() => studioPlusPrice.value?.baseSubscriptionFullTermPrice ?? 0)

  const hasRateUnavailabilityNotification = computed(() =>
    (notificationsData.value ?? []).some(notification =>
      (RC_PLUS_UPSELL_RATE_UNAVAILABILITY_NOTIFICATION_TYPES as readonly string[]).includes(notification.type)
    )
  )

  const isBlockedCountry = computed(() => {
    const countryId = currentAccommodation.value?.countryId
    return countryId ? RC_BLOCKED_COUNTRIES.includes(countryId) : false
  })

  const isPlusUpsellEligible = computed(() => {
    if (isBlockedCountry.value) return false
    if (isCampaignFetching.value) return false
    if (!isNotificationsSuccess.value) return false
    if (hasRateUnavailabilityNotification.value) return false
    const neverHadPlusOrPro = !isPro.value && !isPlus.value
    return neverHadPlusOrPro || isExpiredSubscription.value
  })

  const defaultPaymentMethod = computed(() => existingPaymentMethods.value?.find(method => method.isDefault))

  const hasValidDefaultPaymentMethod = computed(() => {
    const method = defaultPaymentMethod.value
    if (!method) return false
    if (method.type === 'CREDIT_CARD' && 'expired' in method && method.expired) return false
    if (method.type === 'WIRE_TRANSFER') return false
    return true
  })

  const plusUpsellSubscribeAction = computed((): PlusUpsellSubscribeAction => {
    if (!isPlusUpsellEligible.value) return null
    if (!hasAdminOrSupport.value) return 'login-as-admin'
    if (!hasValidDefaultPaymentMethod.value) return 'add-payment-method'
    return 'continue'
  })

  const isEligibilityLoading = computed(
    () =>
      isCampaignFetching.value ||
      isPriceFetching.value ||
      isNotificationsFetching.value ||
      (hasAdminOrSupport.value && isZuoraDataLoading.value) ||
      (hasAdminOrSupport.value && isExistingPaymentMethodsFetching.value)
  )

  const showInlineBanner = computed(
    () => isRcPlusCrossSellEnabled && !isEligibilityLoading.value && isPlusUpsellEligible.value
  )

  const displayPopup = computed(
    () =>
      isPostRcPurchaseSession.value &&
      showInlineBanner.value &&
      !hasClosedPopup.value &&
      !isRemindMeLaterDismissed.value
  )

  watch(
    displayPopup,
    shouldDisplay => {
      if (shouldDisplay && !isPopupPausedForSubscriptionFlow.value) {
        isPopupOpen.value = true
      }
    },
    { immediate: true }
  )

  const openSubscriptionModal = () => {
    if (!isRcPlusCrossSellEnabled) return

    isSubscriptionModalOpen.value = true
  }

  const closeSubscriptionModal = () => {
    isSubscriptionModalOpen.value = false
  }

  const setSubscriptionTrackingSource = (source: RcPlusCrossSellSource) => {
    subscriptionTrackingSource.value = source
  }

  const closePopup = () => {
    isPopupOpen.value = false
    hasClosedPopup.value = true
    isPopupPausedForSubscriptionFlow.value = false
  }

  const dismissPopupRemindMeLater = () => {
    storage.setItem(RC_PLUS_CROSS_SELL_POPUP_REMIND_LATER_DISMISSED_KEY, 'true')
    isRemindMeLaterDismissed.value = true
    closePopup()
  }

  const pausePopupForSubscriptionFlow = () => {
    isPopupOpen.value = false
    isPopupPausedForSubscriptionFlow.value = true
  }

  const openPopup = () => {
    isPopupPausedForSubscriptionFlow.value = false
    isPopupOpen.value = true
  }

  return {
    isEligibilityLoading,
    isPlusUpsellEligible,
    hasValidDefaultPaymentMethod,
    plusUpsellSubscribeAction,
    showInlineBanner,
    studioPlusPrice,
    isPriceFetching,
    defaultPaymentMethod,
    discountedMonthlyPrice,
    monthlyPrice,
    totalPriceBeforeTaxes,
    baseSubscriptionFullTermPrice,
    isSubscriptionModalOpen,
    openSubscriptionModal,
    closeSubscriptionModal,
    subscriptionTrackingSource,
    setSubscriptionTrackingSource,
    displayPopup,
    isPopupOpen,
    closePopup,
    dismissPopupRemindMeLater,
    pausePopupForSubscriptionFlow,
    openPopup,
  }
}
